/**
 * Development & Test Environment Configuration
 * This file is used for local development and testing (ng serve)
 * For production builds, this file is replaced with environment.prod.ts
 */

import { PRISM_NEW_ENDPOINTS } from './prism-new-endpoints';

export const environment = {
  production: false,
  environment: 'test',

  apiUrl: '/api/v1',
  //apiUrl: 'http://localhost:3002/api/v1',

  /** Lead Finder (Python web_scraper, `/ai/scraper/leads`); same-origin through the ng-serve `/ai` proxy. */
  leadFinder: {
    baseUrl: '/ai/scraper/leads/',
  },
  // socketUrl: 'http://localhost:5050',
  socketUrl: 'https://testprism.bridgingtech.com',
  // Google sign-in OAuth client id (public). Empty hides the Google button.
  auth: {
    googleClientId: '565496701197-m6u0lqudqr88u6uroi9jqiii29mblhq8.apps.googleusercontent.com',
  },

  /**
   * prism-new: the rewritten main API (docs: https://testprism.bridgingtech.com/prism-new/docs).
   * A call whose method + path is in `endpoints` goes to `prismNew.apiUrl`; every other call stays on
   * `apiUrl` above. Set `enabled: false` to send everything back to the old API, or delete one line
   * from prism-new-endpoints.ts to send back just that endpoint.
   */
  prismNew: {
    enabled: true,
    // Proxied: `npm run start:prism-local` sends /prism-new to a local backend, `npm start` to testprism.
    apiUrl: '/prism-new/api/v1',
    // apiUrl: '/api/v1',
    /** prism-new's Socket.IO path (its SOCKET_PATH), proxied like /prism-new/ with websockets on. */
    socketPath: '/prism-new/socket.io/',
    endpoints: PRISM_NEW_ENDPOINTS,
  },

  api: {
    timeout: 60000,
    retries: 0,
    retryDelay: 1000,
    enableCache: false,
    cacheTTL: 5 * 60 * 1000,
    withCredentials: true,
  },
  partnerOutbound: {
    enabled: true,
  },

  logging: {
    level: 'DEBUG', // TRACE, DEBUG, INFO, LOG, WARN, ERROR, FATAL, OFF
    serverLoggingUrl: null as string | null,
    enableSourceMaps: true,
    disableConsoleLogging: false,
  },

  socket: {
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  },

  app: {
    name: 'PRISM CRM',
    version: '1.0.0',
    defaultLanguage: 'en',
    supportedLanguages: ['en'],
    dateFormat: 'MM/DD/YYYY',
    timeFormat: 'hh:mm A',
    currency: 'USD',
    timezone: 'America/New_York',
  },

  productServerUrls: [
    {
      name: 'denefits',
      url: 'https://api.denefits.com',
      useAuth: true,
      authToken: '961a943bc3a1a71d307832b2604f416e',
      authKeyname: 'access_token',
      includeIn: 'body',
      withCredentials: false,
    },

    {
      name: 'lendee',
      url: 'https://api.lendee.com/adm/v1',
      useAuth: true,
      authToken:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZG1pbl9pZCI6MTcwLCJpYXQiOjE3NTgxODY4OTh9.FfttQ-3lP5M68eQZyHNNLjpFfWSsTCQLllSA2T5leS0',
      authKeyname: 'access_token',
      includeIn: 'body',
      withCredentials: false,
    },
    {
      name: 'cool-credit',
      url: 'https://testapi.coolcredit.com/',
      useAuth: false,
      withCredentials: false,
      includeIn: 'header',
    },
    {
      name: 'credee',
      url: 'https://testapi.credee.com/admin/',
      useAuth: true,
      authKeyname: 'access_token',
      includeIn: 'body',
      accessTokenKey:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0eXBlIjoiYWNjZXNzIiwicHJvZHVjdCI6IkxPT0tFUl9VUF9TVFVESU8iLCJpYXQiOjE3ODI5OTgyODksImV4cCI6MTc4MzYwMzA4OX0.3Me7Hz-aKm0W2CbjGsudN91ISX5NYfdlFMt0SaeTabQ',
      withCredentials: false,
      xRefreshToken:
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0eXBlIjoicmVmcmVzaCIsInByb2R1Y3QiOiJMT09LRVJfVVBfU1RVRElPIiwiaWF0IjoxNzgyOTk4Mjg5LCJleHAiOjE3ODU1OTAyODl9.GEfUbkpIp2lozcMvw9AklGQGEaUJ9s2CcVamLtb1Ghg',
    },
    {
      name: 'practina',
      url: 'https://staging.practina.com/',
      useAuth: false,
      withCredentials: false,
      includeIn: 'header',
    },
    {
      // Dedicated route for Practina's guest-facing scheduler API (the "Schedule a callback"
      // booking flow in the consultant modal) — kept separate from the generic 'practina'
      // entry above so scoping this one to scheduler_guest/ can't break other Practina calls
      // that resolve their base URL via productServerName: 'practina'.
      name: 'practina-scheduler',
      url: 'https://testbusiness.practina.com/demo_lang/api/scheduler_guest/',
      useAuth: false,
      withCredentials: false,
      includeIn: 'header',
    },
    {
      name: 'finance-mutual',
      url: 'https://testadmin.financemutual.com/api/admin/',
      useAuth: false,
      withCredentials: false
    },
    {
      name: 'recuvery',
      url: 'https://testexternal.recuvery.com/external/prism/',
      useAuth: false,
      withCredentials: false
    },
  ],
  ai: {
    baseUrl: 'https://testprism.bridgingtech.com/ai/bot',
    productServerName: 'ai-bot',
    useAuth: false,
    withCredentials: false,
  },
  aiChatBot: {
    baseUrl: 'https://testprism.bridgingtech.com/ai-chatbot/bot',
    productServerName: 'ai-chatbot',
    useAuth: false,
    withCredentials: false,
  },
  leadGen: {
    baseUrl: 'https://testprism.bridgingtech.com/ai/lead_gen',
    // baseUrl: 'http://192.168.3.157:8004/ai/lead_gen',
    productServerName: 'ai-lead-gen',
    useAuth: false,
    withCredentials: false,
  },
  /** AI Voice browser (WebRTC) calling — see ai_voice/web_call_router.py (/ai/voice/web/*). */
  aiVoice: {
    baseUrl: 'https://testprism.bridgingtech.com/ai/voice/web',
    useAuth: false,
    withCredentials: false,
  },

  /**
   * Live Call Agent (FastAPI `live_call_agent`, mounted under `/ai/live-call`).
   * Routes resolve as `${baseUrl}live-call/api/<endpoint>` — e.g. `.../ai/live-call/api/research`.
   */
  // liveCall: {
  //   baseUrl: 'https://testprism.bridgingtech.com/ai/',
  //   // baseUrl: 'http://127.0.0.1:8004/ai/', // local backend override during development
  //   useAuth: false,
  //   withCredentials: false,
  // },

  /**
   * Live Call Agent (FastAPI `live_call_agent`, mounted under `/ai/live-call`).
   * Routes resolve as `${baseUrl}live-call/api/<endpoint>` — e.g. `.../ai/live-call/api/research`.
   */
  // liveCall: {
  //   baseUrl: 'https://testprism.bridgingtech.com/ai/',
  //   // baseUrl: 'http://127.0.0.1:8004/ai/', // local backend override during development
  //   useAuth: false,
  //   withCredentials: false,
  //   /**
  //    * Idle re-engagement policy for a live demo. When the call goes silent, the
  //    * agent checks in `checkInCount` times spaced `checkInIntervalMs` apart, then
  //    * every `followUpIntervalMs`. Once `maxCheckIns` total go unanswered the call
  //    * ends (goodbye + hangup) — an abandoned open tab must not keep the billed
  //    * Gemini session alive forever. Any speech resets the cycle.
  //    */
  //   idle: {
  //     checkInIntervalMs: 1 * 60 * 1000, // 1 min between the first check-ins
  //     checkInCount: 3, // initial check-ins at 1 / 2 / 3 min
  //     followUpIntervalMs: 7 * 60 * 1000, // then keep asking every 7 min
  //     maxCheckIns: 4, // give up one slot after the 4th unanswered check-in (~17 min silent here)
  //   },
  // },

  /**
   * Live Call Agent (FastAPI `live_call_agent`, mounted under `/ai/live-call`).
   * Routes resolve as `${baseUrl}live-call/api/<endpoint>` — e.g. `.../ai/live-call/api/research`.
   */
  liveCall: {
    // baseUrl: 'https://testprism.bridgingtech.com/ai/',
    // baseUrl: 'http://127.0.0.1:8004/', // direct local backend (this machine ONLY)
    // Same-origin via the ng-serve dev proxy (proxy.conf.json "/ai" →
    // http://127.0.0.1:8004, which strips the /ai prefix). Unlike a hardcoded
    // 127.0.0.1 URL, this also works from OTHER devices on your network: run
    // `npm start -- --host 0.0.0.0` and open http://<your-ip>:4200 — API +
    // WebSockets ride the page's origin.
    //
    // REQUIRED for the dockerised recorder. The recorder's headless Chromium
    // runs INSIDE a container, where `127.0.0.1` is the container itself and
    // nothing listens on 8004 — so a hardcoded loopback baseUrl let the host
    // tab record fine while every containerised recording died with "start
    // signal timeout". Origin-relative means the recorder page reaches the same
    // backend as the page that served it, whichever host that is.
    baseUrl: '/ai/',
    useAuth: false,
    withCredentials: false,
    /**
     * TOUR WORKER base for the live co-browse stream (`/api/tour/stream`). In container mode
     * the browser runs on the worker, so the prospect's stream connects there directly. Set
     * this to the worker's public base UP TO the backend prefix (the code appends
     * `/api/tour/stream`), http(s) is swapped to ws(s) — e.g. 'https://tour.credee.com/ai/live-call'.
     * Empty = same-origin (dev / inprocess), where the stream rides the page's own backend.
     */
    tourStreamOrigin: '',
    /**
     * Google reCAPTCHA v2 INVISIBLE site key for the live booking flow
     * (book_credee_demo_slot mints a fresh single-use token per attempt).
     * This is the credee.com scheduler's own public key (window.gCaptchaKey in
     * their theme bundle) — the same key serves BOTH prod and test, and it must
     * match the secret testapi/api verifies. Empty = reCAPTCHA not loaded and
     * the backend refuses bookings with a structured captcha_required error.
     */
    recaptchaSiteKey: '6LcBn2sqAAAAAMa1rVf-CqciCaQBIg5G5-0eG4DP',
    /**
     * Idle re-engagement policy for a live demo. When the call goes silent, the
     * agent checks in `checkInCount` times spaced `checkInIntervalMs` apart, then
     * every `followUpIntervalMs`. Once `maxCheckIns` total go unanswered the call
     * ends (goodbye + hangup) — an abandoned open tab must not keep the billed
     * Gemini session alive forever. Any speech resets the cycle.
     *
     * TEST BUDGET: 2 MINUTES of continuous silence → call ends. Check-in at 1 min,
     * give-up slot at 2 min. These four keys ARE the timeout — the sum below is
     * the only clock; change them and the hangup moves with them. (The goodbye
     * then gets IDLE_GIVEUP_GRACE_MS to be spoken before the hard cut.)
     */
    idle: {
      checkInIntervalMs: 1 * 60 * 1000, // "are you there?" at 1 min
      checkInCount: 1,
      followUpIntervalMs: 1 * 60 * 1000, // +1 min unanswered → give up
      maxCheckIns: 1, // → hangup at 2 min of silence
    },
  },
  aiLiveCall: {
    baseUrl: 'https://testprism.bridgingtech.com/ai/live-call',
    productServerName: 'ai-live-call',
    useAuth: false,
    withCredentials: false,
  },

  externalApps: {
    matrixUrl: 'https://matrix.denefits.com',
    // Product dashboards embedded by ProductDashboardFrameComponent, keyed by
    // ProductConfig.id. A product left out shows "currently not available".
    productDashboards: {
      denefits: 'https://testadminv2.denefits.com/app/dashboard',
      finance_mutual: 'https://testadmin.financemutual.com/app/dashboard?country_id=1&is_dummy=0&timezone=-700',
      recuvery: 'https://testadmin.recuvery.com/app/dashboard?country_id=1&timezone=-700&cocu_id=1&is_dummy=0',
      practina: 'https://testadmin.practina.com/dashboard?isLiveData=1',
      credee: 'https://test.credee.com/admin/app/dashboard',
      lendee: 'https://testadmin.lendee.com/home',
      'cool-credit': 'https://testadmin.coolcredit.com/home',
    },
  },

  // Public web URLs of the product sites the demo is embedded in and links out to
  // (read by PRODUCT_CONFIGS in core/services/product-config.service.ts).
  //   baseUrl   — the embedding origin share/invite links and the `credee:url-sync`
  //               postMessage are built on (ProductConfig.embedBaseUrl, with credeeWeb
  //               as the last-resort fallback). A wrapper page overrides its own entry
  //               by passing `?baseUrl=`; see PARENT_URL_SYNC.md.
  //   signupUrl — where the demo's "get started" CTA lands.
  // Each URL is spelled out whole per environment rather than derived from baseUrl:
  // the PATHS differ across environments too (Lendee's dev onboarding keeps a `/user/`
  // prefix), and Lendee signs up on a different host from the one it embeds on.
  credeeWeb: {
    baseUrl: 'https://dev.credee.com',
  },
  lendeeWeb: {
    baseUrl: 'https://dev.lendeeapp.com',
    signupUrl: 'https://dev.lendeeapp.com/user/get-started',
  },
  practinaWeb: {
    baseUrl: 'https://dev.practina.com',
    signupUrl: 'https://dev.practina.com/demo/home?signup=true&country_id=2',
  },

  product_postMessage: {
    DENEFITS: {
      server_url: 'https://testadminv2.denefits.com',
    }
  },
  microAppService_url: {
    whitelabel: {
      // url: 'https://testprism.bridgingtech.com/gateway/api/v1/whitelabel',
      url: 'http://localhost:3050/api/v1/whitelabel',
      useAuth: false,
      withCredentials: true
    },
    callAuditor: {
      // url: 'https://testprism.bridgingtech.com/gateway/api/v1/call-auditor',
      url: 'http://localhost:3050/api/v1/call-auditor', // local prism-microservices gateway
      useAuth: false,
      withCredentials: true,
    },
  },

  /** Full URL for workspace chat (POST). Uses gateway on test so chat works while whitelabel microservice is localhost. */
  workspaceWhitelabelChatUrl:
    'https://testprism.bridgingtech.com/gateway/api/v1/whitelabel/chat',

  /**
   * Embed chat iframe (`/embed/chat`).
   * - allowedParentOrigins: postMessage senders allowed to pass auth_token. Empty = same-origin only.
   * - publicAppOrigin: optional absolute origin for building embed URLs when not same window (e.g. Storybook).
   */
  enterpriseEmbed: {
    allowedParentOrigins: [] as string[],
    publicAppOrigin: '' as string,
  },

  pythonApi: {
    baseUrl: 'https://testprism.bridgingtech.com/',
    socketUrl: 'https://testprism.bridgingtech.com/ai/lead_gen/ws/pipeline/socket.io/',
    // baseUrl: 'http://192.168.3.157:8004/',
    // socketUrl: 'http://192.168.3.157:8004/ai/lead_gen/ws/pipeline/socket.io/',
    // baseUrl: 'https://testprism.bridgingtech.com/ai/',
    // socketUrl: 'https://testprism.bridgingtech.com/ai/socket.io/',
    withCredentials: false,
  },

  credee_signup_url: {
    business: 'https://dev.credee.com',
  },
};

export type Environment = typeof environment;
